(() => {
  'use strict';
  const instances = new WeakMap();
  const widths = [480, 800, 1000, 1200, 1600];
  const sizes = '(max-width: 760px) 100vw, (max-width: 1100px) 57vw, 60vw';

  const sizedUrl = (source, width) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.href;
  };
  const fileIdentity = (source) => {
    // Shopify serves the same verified file through both the storefront's
    // /cdn/shop/files proxy and cdn.shopify.com/s/files. Keep its SSR request
    // when only that alias or an image-size query differs.
    const url = new URL(source, window.location.origin);
    return url.pathname.split('/').pop();
  };
  const responsiveImage = (image, item, index) => {
    image.src = sizedUrl(item.src, 1000);
    image.srcset = widths.map((width) => `${sizedUrl(item.src, width)} ${width}w`).join(', ');
    image.sizes = sizes;
    image.alt = item.alt || 'JBL Charge 6';
    image.width = Number(item.width) || 1600;
    image.height = Number(item.height) || 1600;
    image.loading = index < 2 ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.fetchPriority = index === 0 ? 'high' : 'auto';
  };
  const normalize = (images) => (Array.isArray(images) ? images : []).filter((item) => {
    if (!item?.src) return false;
    try {
      const url = new URL(item.src, window.location.origin);
      return url.protocol === 'https:' && (url.origin === window.location.origin || /(^|\.)shopify\.com$/.test(url.hostname));
    } catch (_) { return false; }
  }).map((item, index) => ({ ...item, hero: index === 0 && item.hero === true }));

  function create(page, initialImages) {
    if (!page) return null;
    if (instances.has(page)) {
      const previous = instances.get(page);
      if (initialImages) previous.setImages(initialImages);
      return previous;
    }
    const gallery = page.querySelector('[data-gallery]');
    const stage = gallery?.querySelector('.jbl-gallery__stage');
    const track = gallery?.querySelector('[data-gallery-track]');
    if (!gallery || !stage || !track) return null;
    const t = window.JblText.create(page);
    const query = (selector) => page.querySelector(selector);
    const dialog = query('[data-zoom-dialog]');
    const zoomImage = query('[data-zoom-image]');
    const thumbnails = gallery.querySelector('.jbl-thumbnails');
    const dots = gallery.querySelector('.jbl-gallery-dots');
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let images = [];
    let imageIndex = 0;
    let signature = '';
    let scrollFrame = 0;
    let resizeFrame = 0;
    let suppressZoomUntil = 0;
    let lastNativeScroll = 0;
    let pointer = null;
    let guardTouch = null;
    let previouslyFocused = null;
    let previousOverflow = '';
    let decoding = new WeakSet();
    gallery.classList.add('jbl-gallery--snap');
    gallery.classList.remove('is-hero-angle');

    const preload = (index) => {
      [index - 1, index, index + 1, index + 2].forEach((number) => {
        const image = track.children[number]?.querySelector('img');
        if (!image || decoding.has(image)) return;
        decoding.add(image);
        image.loading = 'eager';
        if (image.decode) image.decode().catch(() => {});
      });
    };

    const updateZoom = () => {
      const item = images[imageIndex];
      if (zoomImage && item && dialog?.open) {
        zoomImage.src = sizedUrl(item.src, 1600);
        zoomImage.alt = item.alt;
        zoomImage.decoding = 'async';
      }
      const previous = query('[data-zoom-prev]');
      const next = query('[data-zoom-next]');
      if (previous) previous.disabled = imageIndex === 0;
      if (next) next.disabled = imageIndex >= images.length - 1;
      const counter = query('[data-zoom-counter]');
      if (counter) counter.textContent = `${images.length ? imageIndex + 1 : 0} / ${images.length}`;
    };

    const setActive = (index, moveThumbnail = false) => {
      imageIndex = Math.max(0, Math.min(index, images.length - 1));
      [...track.children].forEach((slide, number) => {
        const active = number === imageIndex;
        slide.classList.toggle('is-active', active);
        const button = slide.querySelector('[data-zoom-open]');
        if (button) button.tabIndex = active ? 0 : -1;
        const image = slide.querySelector('img');
        if (image) image.toggleAttribute('data-main-image', active);
      });
      gallery.querySelectorAll('[data-thumbnail], [data-gallery-dot]').forEach((button) => {
        const active = Number(button.dataset.galleryDot ?? button.dataset.galleryIndex) === imageIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', String(active));
      });
      const previous = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      if (previous) previous.disabled = imageIndex === 0;
      if (next) next.disabled = imageIndex >= images.length - 1;
      const counter = gallery.querySelector('[data-gallery-counter]');
      if (counter) {
        counter.textContent = `${images.length ? imageIndex + 1 : 0} `;
        const total = document.createElement('span');
        total.textContent = `/ ${images.length}`;
        counter.append(total);
      }
      if (moveThumbnail && thumbnails?.clientWidth) {
        const selected = thumbnails.querySelector(`[data-gallery-index="${imageIndex}"]`);
        if (selected) thumbnails.scrollTo({
          left: selected.offsetLeft - thumbnails.offsetLeft - (thumbnails.clientWidth - selected.offsetWidth) / 2,
          behavior: motionPreference.matches ? 'auto' : 'smooth'
        });
      }
      preload(imageIndex);
      updateZoom();
    };

    const goTo = (index, immediate = false) => {
      if (!images.length) return;
      const target = Math.max(0, Math.min(index, images.length - 1));
      preload(target);
      // The whole strip already exists. The browser moves pixels immediately;
      // image decode is only a warm-up and never gates the gesture or controls.
      track.scrollTo({ left: target * track.clientWidth, behavior: immediate || motionPreference.matches ? 'auto' : 'smooth' });
      if (immediate || dialog?.open) setActive(target, true);
    };

    const closeZoom = () => { if (dialog?.open) dialog.close(); };
    const openZoom = (event) => {
      if (!dialog || !images.length || Date.now() < suppressZoomUntil) return;
      if (event?.detail && Date.now() - lastNativeScroll < 180) return;
      if (dialog.open || typeof dialog.showModal !== 'function') return;
      previouslyFocused = document.activeElement;
      previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      updateZoom();
    };

    const setImages = (nextImages) => {
      const normalized = normalize(nextImages);
      if (!normalized.length) return;
      const nextSignature = JSON.stringify(normalized.map((item) => [item.src, item.alt, item.hero]));
      if (signature === nextSignature) return;
      signature = nextSignature;
      images = normalized;
      imageIndex = 0;
      cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
      decoding = new WeakSet();
      const slides = document.createDocumentFragment();
      const thumbnailItems = document.createDocumentFragment();
      const dotItems = document.createDocumentFragment();
      images.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = `jbl-gallery__slide${item.hero ? ' is-hero' : ''}${index === 0 ? ' is-active' : ''}`;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', t('gallery.slide'));
        slide.setAttribute('aria-label', t('gallery.position', { index: index + 1, count: images.length }));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jbl-gallery__image-button';
        button.dataset.zoomOpen = '';
        button.setAttribute('aria-label', t('gallery.enlarge', { index: index + 1 }));
        const image = document.createElement('img');
        image.className = 'jbl-gallery__image';
        responsiveImage(image, item, index);
        button.append(image);
        slide.append(button);
        slides.append(slide);
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'jbl-thumbnail';
        thumb.dataset.thumbnail = '';
        thumb.dataset.galleryIndex = String(index);
        thumb.dataset.imageUrl = sizedUrl(item.src, 1600);
        thumb.dataset.imageAlt = item.alt;
        thumb.setAttribute('aria-label', t('gallery.show', { index: index + 1 }));
        const thumbImage = document.createElement('img');
        thumbImage.src = sizedUrl(item.src, 160);
        thumbImage.alt = '';
        thumbImage.width = 80;
        thumbImage.height = 80;
        thumbImage.loading = 'lazy';
        thumbImage.decoding = 'async';
        thumb.append(thumbImage);
        thumbnailItems.append(thumb);
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.dataset.galleryDot = String(index);
        dot.setAttribute('aria-label', t('gallery.show', { index: index + 1 }));
        dotItems.append(dot);
      });
      track.replaceChildren(slides);
      thumbnails?.replaceChildren(thumbnailItems);
      dots?.replaceChildren(dotItems);
      track.scrollTo({ left: 0, behavior: 'auto' });
      setActive(0);
      lastNativeScroll = 0;
    };

    track.addEventListener('scroll', () => {
      lastNativeScroll = Date.now();
      if (guardTouch && Math.abs(track.scrollLeft - guardTouch.left) > 2) {
        guardTouch.moved = true;
        suppressZoomUntil = Date.now() + 500;
      }
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        if (track.clientWidth) {
          const visibleIndex = Math.max(0, Math.min(Math.round(track.scrollLeft / track.clientWidth), images.length - 1));
          if (visibleIndex !== imageIndex) setActive(visibleIndex);
        }
      });
    }, { passive: true });
    track.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    }, { passive: true });
    track.addEventListener('pointermove', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 8) {
        pointer.moved = true;
        suppressZoomUntil = Date.now() + 500;
      }
    }, { passive: true });
    const endPointer = (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      if (pointer.moved || event.type === 'pointercancel') suppressZoomUntil = Date.now() + 500;
      pointer = null;
    };
    track.addEventListener('pointerup', endPointer, { passive: true });
    track.addEventListener('pointercancel', endPointer, { passive: true });
    // These passive listeners only reject the click synthesized after a swipe
    // (or a vertical page scroll). They never move the strip or replace a src.
    track.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      guardTouch = { x: event.touches[0].clientX, y: event.touches[0].clientY, left: track.scrollLeft, moved: false };
    }, { passive: true });
    track.addEventListener('touchmove', (event) => {
      if (!guardTouch || !event.touches.length) return;
      const touch = event.touches[0];
      if (Math.hypot(touch.clientX - guardTouch.x, touch.clientY - guardTouch.y) > 8) {
        guardTouch.moved = true;
        suppressZoomUntil = Date.now() + 500;
      }
    }, { passive: true });
    const endTouch = () => {
      if (guardTouch?.moved) suppressZoomUntil = Date.now() + 500;
      guardTouch = null;
    };
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', endTouch, { passive: true });
    gallery.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !gallery.contains(button)) return;
      if (button.hasAttribute('data-gallery-prev')) goTo(imageIndex - 1);
      else if (button.hasAttribute('data-gallery-next')) goTo(imageIndex + 1);
      else if (button.hasAttribute('data-gallery-dot')) goTo(Number(button.dataset.galleryDot));
      else if (button.hasAttribute('data-thumbnail')) goTo(Number(button.dataset.galleryIndex));
      else if (button.hasAttribute('data-zoom-open')) openZoom(event);
    });
    query('[data-zoom-prev]')?.addEventListener('click', () => goTo(imageIndex - 1, true));
    query('[data-zoom-next]')?.addEventListener('click', () => goTo(imageIndex + 1, true));
    query('[data-zoom-close]')?.addEventListener('click', closeZoom);
    dialog?.addEventListener('close', () => {
      document.documentElement.style.overflow = previousOverflow;
      const target = previouslyFocused?.isConnected ? previouslyFocused : track.children[imageIndex]?.querySelector('button');
      target?.focus({ preventScroll: true });
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeZoom();
    });
    const keyboard = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || /INPUT|SELECT|TEXTAREA/.test(event.target.tagName)) return;
      let target;
      if (event.key === 'ArrowLeft') target = imageIndex - 1;
      else if (event.key === 'ArrowRight') target = imageIndex + 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = images.length - 1;
      else return;
      event.preventDefault();
      goTo(target, !!dialog?.open);
    };
    gallery.addEventListener('keydown', keyboard);
    dialog?.addEventListener('keydown', keyboard);
    const resize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => goTo(imageIndex, true));
    };
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(track);
    else window.addEventListener('resize', resize, { passive: true });

    const api = { setImages, getIndex: () => imageIndex };
    instances.set(page, api);
    const ssrImages = [...track.querySelectorAll('.jbl-gallery__slide img')].map((image, index) => ({
      src: image.dataset.gallerySource || image.src,
      alt: image.alt,
      width: image.width,
      height: image.height,
      hero: index === 0 && image.closest('.jbl-gallery__slide').classList.contains('is-hero')
    }));
    // Hydrate the server-rendered strip when the data is identical, preserving
    // the first photo's in-flight request and its loading priority.
    const normalized = normalize(initialImages?.length ? initialImages : ssrImages);
    const sameSsr = normalized.length === ssrImages.length && normalized.every((item, index) =>
      fileIdentity(item.src) === fileIdentity(ssrImages[index].src));
    if (sameSsr && normalized.length) {
      images = normalized;
      signature = JSON.stringify(images.map((item) => [item.src, item.alt, item.hero]));
      [...track.querySelectorAll('.jbl-gallery__slide')].forEach((slide, index) => {
        slide.classList.toggle('is-hero', images[index].hero);
        const image = slide.querySelector('img');
        if (image) image.alt = images[index].alt;
      });
      setActive(0);
    } else setImages(normalized);
    return api;
  }
  window.JblGallery = Object.freeze({ create });
})();
